import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { usePlant } from "@/context/PlantContext";
import { DollarSign, TrendingUp, Truck, Clock, Users, AlertCircle, Percent, Layers } from "lucide-react";

const $ = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Cell = ({ label, value, sub, tone = "brand", Icon, testid }) => {
  const tones = {
    brand:   "bg-primary-orange/15 border-primary-orange/30 text-primary-orange",
    good:    "bg-emerald-500/15 border-emerald-500/30 text-emerald-500",
    warn:    "bg-amber-500/15 border-amber-500/30 text-amber-500",
    danger:  "bg-red-500/15 border-red-500/30 text-red-500",
    info:    "bg-sky-500/15 border-sky-500/30 text-sky-500",
  };
  return (
    <Card data-testid={testid} className="card-v2 p-5 kpi-hover">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-tag">{label}</div>
          <div className="mono text-2xl md:text-3xl font-bold tracking-tight mt-2 text-ink">{value}</div>
          {sub && <div className="text-xs text-muted-industrial mt-1 truncate">{sub}</div>}
        </div>
        <div className={`w-11 h-11 rounded-lg border flex items-center justify-center shrink-0 ${tones[tone]}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>
    </Card>
  );
};

export default function Executive() {
  const { plantId } = usePlant();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/executive/kpis${plantId ? `?plant_id=${plantId}` : ""}`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [plantId]);

  if (loading || !data) {
    return <div className="p-8 label-tag text-primary-orange pulse-orange">Loading executive KPIs…</div>;
  }
  const { revenue, gross_profit, margin_pct, material_cost, trucks, times, top_customers, outstanding_total, outstanding_count } = data;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="executive-page">
      <div>
        <div className="label-tag">ARMORBATCH GLOBAL · EXECUTIVE VIEW</div>
        <h1 className="heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
          Management <span className="text-primary-orange">Dashboard</span>
        </h1>
        <p className="text-muted-industrial text-sm mt-1">Revenue, profit, utilisation &amp; ageing at a glance.</p>
      </div>

      {/* Revenue row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Cell testid="exec-rev-today" label="Revenue Today" value={$(revenue.today)} sub={`Material $${material_cost.today.toFixed(0)} · Profit $${gross_profit.today.toFixed(0)}`} tone="brand" Icon={DollarSign} />
        <Cell testid="exec-rev-week" label="Revenue · Last 7 Days" value={$(revenue.week)} sub={`Profit $${gross_profit.week.toFixed(0)} · ${margin_pct.week}% margin`} tone="good" Icon={TrendingUp} />
        <Cell testid="exec-rev-month" label="Revenue · Month-to-date" value={$(revenue.month)} sub={`Profit $${gross_profit.month.toFixed(0)} · ${margin_pct.month}% margin`} tone="good" Icon={TrendingUp} />
      </div>

      {/* Utilisation & ops */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Cell testid="exec-util" label="Fleet Utilisation" value={`${trucks.utilisation_pct}%`} sub={`${trucks.active}/${trucks.total} trucks active`} tone={trucks.utilisation_pct >= 50 ? "good" : "warn"} Icon={Truck} />
        <Cell testid="exec-wait" label="Avg Wait Time" value={`${times.avg_wait_min} min`} sub="On-site (docket arrive → complete)" tone={times.avg_wait_min <= 45 ? "good" : "warn"} Icon={Clock} />
        <Cell testid="exec-load" label="Avg Load Time" value={`${times.avg_load_min} min`} sub="Batch start → truck loaded" tone="info" Icon={Layers} />
        <Cell testid="exec-outstanding" label="Outstanding Debtors" value={$(outstanding_total)} sub={`${outstanding_count} unpaid invoices`} tone={outstanding_count > 0 ? "danger" : "good"} Icon={AlertCircle} />
      </div>

      {/* Margin card */}
      <Card className="card-v2 p-6" data-testid="exec-margin-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-tag">Gross Margin</div>
            <div className="heading text-2xl font-black uppercase">Profitability · MTD</div>
          </div>
          <Percent className="w-6 h-6 text-primary-orange" />
        </div>
        <div className="grid grid-cols-3 gap-6">
          {[
            ["Today", revenue.today, material_cost.today, gross_profit.today, margin_pct.today],
            ["Week", revenue.week, material_cost.week, gross_profit.week, margin_pct.week],
            ["Month", revenue.month, material_cost.month, gross_profit.month, margin_pct.month],
          ].map(([label, rev, cost, profit, pct]) => (
            <div key={label} className="border-l-2 border-primary-orange pl-4">
              <div className="label-tag">{label}</div>
              <div className="mono text-2xl font-bold text-ink mt-1">{$(profit)}</div>
              <div className="text-xs text-muted-industrial mt-1">{pct}% of ${rev.toFixed(0)} revenue</div>
              <div className="mt-2 h-1.5 bg-surface-2 rounded-full overflow-hidden">
                <div className="h-full bg-primary-orange" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Top customers */}
      <Card className="card-v2 p-6" data-testid="exec-top-customers">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-primary-orange" />
          <div className="label-tag">Top 10 Customers · Month-to-date</div>
        </div>
        {top_customers.length === 0 ? (
          <div className="text-sm text-muted-industrial">No deliveries yet this month.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-industrial">
                <th className="label-tag pb-2">Customer</th>
                <th className="label-tag pb-2 text-right">m³</th>
                <th className="label-tag pb-2 text-right">Revenue</th>
                <th className="label-tag pb-2 text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {top_customers.map((c, i) => (
                <tr key={c.customer_id} data-testid={`top-cust-${i}`} className="border-t border-industrial hover:bg-surface-2">
                  <td className="py-2.5 font-semibold text-ink">{c.name}</td>
                  <td className="py-2.5 text-right mono">{c.m3.toFixed(1)}</td>
                  <td className="py-2.5 text-right mono">{$(c.revenue)}</td>
                  <td className={`py-2.5 text-right mono ${c.profit >= 0 ? "text-emerald-500" : "text-red-500"}`}>{$(c.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
